import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import FileUploader, { type UploadedFileRecord } from '@/components/feature/FileUploader';
import InlineUploadDropzone from '@/components/feature/InlineUploadDropzone';
import ChangeOrderAttachmentList from '@/components/feature/ChangeOrderAttachmentList';
import type { InstructionAttachment } from '@/components/feature/InstructionAttachmentThumbnails';
import { openProjectFileInNewTab } from '@/lib/storageSignedUrl';
import ChangeOrderPricingFields from '@/components/feature/ChangeOrderPricingFields';
import {
  type ChangeOrderPricingMode,
  type ChangeOrderPricingType,
  buildLockedCommercialUpdate,
  inferChangeOrderPricingType,
  isLockedChangeOrderStatus,
  persistFormPricing,
  toAbsoluteMoneyInput,
} from '@/lib/changeOrderPricing';
import { emitChangeOrdersSync, unlinkChangeOrderAttachment } from '@/lib/changeOrderUtils';
import {
  type ChangeOrderInvoiceStatus,
  CO_INVOICE_STATUSES,
  CO_INVOICE_STATUS_LABELS,
  buildInvoicePayload,
  CO_INVOICE_MIGRATION_HINT,
  hasInvoiceFieldChanges,
  isInvoiceColumnError,
  normalizeInvoiceStatus,
  uploadChangeOrderInvoiceFile,
} from '@/lib/changeOrderInvoice';

interface ChangeOrderRecord {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  client_description: string | null;
  reason: string | null;
  status: string;
  requires_client_approval: boolean;
  client_visible: boolean | null;
  schedule_impact: string | null;
  current_finish_description: string | null;
  new_finish_description: string | null;
  co_number: string | null;
  builder_cost: number | null;
  client_price: number | null;
  internal_note: string | null;
  invoice_status: string | null;
  invoice_url: string | null;
  invoice_file_id: string | null;
  invoiced_at: string | null;
  paid_at: string | null;
  updated_at: string;
}

interface Props {
  show: boolean;
  co: ChangeOrderRecord | null;
  attachments: InstructionAttachment[];
  invoiceFieldsAvailable?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const EDITABLE_STATUSES_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_client_approval: 'Pending Client Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  closed: 'Closed',
};

function isLocked(status: string): boolean {
  return isLockedChangeOrderStatus(status);
}

function isPendingApproval(status: string): boolean {
  return ['pending_client_approval', 'sent_to_client'].includes(status);
}

async function checkDuplicateCoNumber(projectId: string, number: string, excludeId?: string): Promise<boolean> {
  const { data } = await supabase
    .from('jphph_change_orders')
    .select('id')
    .eq('project_id', projectId)
    .eq('co_number', number.trim())
    .eq('is_deleted', false);
  if (!data || data.length === 0) return false;
  if (excludeId) {
    return data.some((row) => (row as Record<string, unknown>).id !== excludeId);
  }
  return true;
}

export default function EditChangeOrderModal({ show, co, attachments, invoiceFieldsAvailable = true, onClose, onSaved }: Props) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [clientDescription, setClientDescription] = useState('');
  const [reason, setReason] = useState('');
  const [currentFinish, setCurrentFinish] = useState('');
  const [newFinish, setNewFinish] = useState('');
  const [scheduleImpact, setScheduleImpact] = useState('');
  const [builderCost, setBuilderCost] = useState('');
  const [clientPrice, setClientPrice] = useState('');
  const [pricingMode, setPricingMode] = useState<ChangeOrderPricingMode>('manual');
  const [pricingType, setPricingType] = useState<ChangeOrderPricingType>('addition');
  const [clientPriceError, setClientPriceError] = useState<string | null>(null);
  const [builderCostError, setBuilderCostError] = useState<string | null>(null);
  const [internalNote, setInternalNote] = useState('');
  const [status, setStatus] = useState('draft');
  const [requiresClientApproval, setRequiresClientApproval] = useState(true);
  const [clientVisible, setClientVisible] = useState(false);
  const [coNumber, setCoNumber] = useState('');
  const [coNumberError, setCoNumberError] = useState('');
  const [coNumberTouched, setCoNumberTouched] = useState(false);

  const [invoiceStatus, setInvoiceStatus] = useState<ChangeOrderInvoiceStatus>('not_invoiced');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [invoiceFileId, setInvoiceFileId] = useState<string | null>(null);
  const [invoiceFileName, setInvoiceFileName] = useState('');
  const [invoiceFileUrl, setInvoiceFileUrl] = useState('');
  const [invoiceFileStorageBucket, setInvoiceFileStorageBucket] = useState<string | null>(null);
  const [invoiceFileStoragePath, setInvoiceFileStoragePath] = useState<string | null>(null);
  const [projectFileOptions, setProjectFileOptions] = useState<
    Array<{ id: string; file_name: string; file_url: string; storage_bucket: string | null; storage_path: string | null }>
  >([]);
  const [invoiceUploadLoading, setInvoiceUploadLoading] = useState(false);
  const [invoiceUploadError, setInvoiceUploadError] = useState('');

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileRecord[]>([]);
  const [managedAttachments, setManagedAttachments] = useState<InstructionAttachment[]>([]);
  const [showFileUploader, setShowFileUploader] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState('');

  // Locked / pending helpers
  const locked = useMemo(() => (co ? isLocked(co.status) : false), [co]);
  const pendingApproval = useMemo(() => (co ? isPendingApproval(co.status) : false), [co]);
  const canVoid = useMemo(() => Boolean(co && ['approved', 'client_approved', 'closed', 'completed'].includes(co.status)), [co]);

  // Populate form when CO opens or changes
  useEffect(() => {
    if (!show || !co) return;
    setTitle(co.title || '');
    setDescription(co.description || '');
    setClientDescription(co.client_description || '');
    setReason(co.reason || '');
    setCurrentFinish(co.current_finish_description || '');
    setNewFinish(co.new_finish_description || '');
    setScheduleImpact(co.schedule_impact || '');
    setPricingType(inferChangeOrderPricingType(co.builder_cost, co.client_price, {
      title: co.title,
      reason: co.reason,
    }));
    setBuilderCost(toAbsoluteMoneyInput(co.builder_cost));
    setClientPrice(toAbsoluteMoneyInput(co.client_price));
    setPricingMode('manual');
    setClientPriceError(null);
    setBuilderCostError(null);
    setInternalNote(co.internal_note || '');
    setStatus(co.status);
    setRequiresClientApproval(co.requires_client_approval);
    setClientVisible(co.client_visible ?? false);
    setCoNumber(co.co_number || '');
    setCoNumberError('');
    setCoNumberTouched(false);
    setInvoiceStatus(normalizeInvoiceStatus(co.invoice_status));
    setInvoiceUrl(co.invoice_url || '');
    setInvoiceFileId(co.invoice_file_id || null);
    setInvoiceFileName('');
    setInvoiceFileUrl('');
    setInvoiceFileStorageBucket(null);
    setInvoiceFileStoragePath(null);
    setInvoiceUploadError('');
    setUploadedFiles([]);
    setError('');
    setSuccess('');
    setVoidReason('');
    setVoidError('');
    setVoiding(false);
  }, [show, co?.id, co?.invoice_status, co?.invoice_url, co?.invoice_file_id, co?.updated_at]);

  useEffect(() => {
    setManagedAttachments(attachments.filter((f) => f.photo_type !== 'co_invoice'));
  }, [attachments]);

  useEffect(() => {
    if (!show || !co) return;
    let cancelled = false;

    async function loadInvoiceContext() {
      const { data: files } = await supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, photo_type, storage_bucket, storage_path')
        .eq('project_id', co!.project_id)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(100);

      if (cancelled) return;

      const options = (files || [])
        .filter((f) => (f as { photo_type?: string | null }).photo_type !== 'co_invoice')
        .map((f) => ({
          id: (f as { id: string }).id,
          file_name: (f as { file_name: string }).file_name,
          file_url: (f as { file_url: string }).file_url,
          storage_bucket: (f as { storage_bucket?: string | null }).storage_bucket ?? null,
          storage_path: (f as { storage_path?: string | null }).storage_path ?? null,
        }));
      setProjectFileOptions(options);

      const fileId = co!.invoice_file_id;
      if (fileId) {
        const linked = (files || []).find((f) => (f as { id: string }).id === fileId) as
          | { file_name: string; file_url: string; storage_bucket?: string | null; storage_path?: string | null }
          | undefined;
        if (linked) {
          setInvoiceFileName(linked.file_name);
          setInvoiceFileUrl(linked.file_url);
          setInvoiceFileStorageBucket(linked.storage_bucket ?? null);
          setInvoiceFileStoragePath(linked.storage_path ?? null);
        } else {
          const { data: linkedFile } = await supabase
            .from('jphph_project_files')
            .select('file_name, file_url, storage_bucket, storage_path')
            .eq('id', fileId)
            .maybeSingle();
          if (!cancelled && linkedFile) {
            setInvoiceFileName((linkedFile as { file_name: string }).file_name);
            setInvoiceFileUrl((linkedFile as { file_url: string }).file_url);
            setInvoiceFileStorageBucket((linkedFile as { storage_bucket?: string | null }).storage_bucket ?? null);
            setInvoiceFileStoragePath((linkedFile as { storage_path?: string | null }).storage_path ?? null);
          }
        }
      }
    }

    loadInvoiceContext();
    return () => {
      cancelled = true;
    };
  }, [show, co?.id, co?.project_id, co?.invoice_file_id]);

  const validationErrors = useMemo(() => {
    const errs: string[] = [];
    if (!title.trim()) errs.push('Title is required');
    if (!coNumber.trim()) errs.push('CO Number is required');
    if (coNumberError) errs.push(coNumberError);
    if (!reason.trim()) errs.push('Reason for change is required');
    // Finish / description remain locked for approved COs; Client Price stays editable.
    if (!locked) {
      if (!currentFinish.trim()) errs.push('Current finish is required');
      if (!newFinish.trim()) errs.push('New finish is required');
      if (requiresClientApproval && !clientDescription.trim()) errs.push('Client description is required when client approval is required');
    }
    const pricingResult = persistFormPricing(builderCost, clientPrice, pricingType, {
      clientRequired: requiresClientApproval,
    });
    if (pricingResult.error) errs.push(pricingResult.error);
    return errs;
  }, [title, reason, currentFinish, newFinish, clientDescription, clientPrice, builderCost, pricingType, requiresClientApproval, coNumber, coNumberError, locked]);

  const isValid = validationErrors.length === 0;

  const handleClose = useCallback(() => {
    setError('');
    setSuccess('');
    setCoNumberError('');
    setUploadedFiles([]);
    onClose();
  }, [onClose]);

  const handleFileUploadComplete = useCallback((records: UploadedFileRecord[]) => {
    setUploadedFiles((prev) => [...prev, ...records]);
    setManagedAttachments((prev) => [
      ...prev,
      ...records.map((f) => ({
        id: f.id,
        file_url: f.file_url,
        file_name: f.file_name,
        file_type: f.file_type,
        photo_type: f.photo_type ?? null,
        caption: f.note || null,
        is_client_visible: f.is_client_visible ?? null,
        change_order_id: f.change_order_id ?? null,
        created_at: f.created_at ?? null,
        storage_bucket: f.storage_bucket ?? null,
        storage_path: f.storage_path ?? null,
      })),
    ]);
    setShowFileUploader(false);
  }, []);

  const handleManagedVisibilityUpdated = useCallback((
    fileId: string,
    next: { photo_type: string; is_client_visible: boolean }
  ) => {
    setManagedAttachments((prev) =>
      prev.map((file) => (file.id === fileId ? { ...file, ...next } : file))
    );
    setUploadedFiles((prev) =>
      prev.map((file) => (file.id === fileId ? { ...file, ...next } : file))
    );
  }, []);

  const handleDeleteManagedAttachment = useCallback(async (file: InstructionAttachment) => {
    if (!window.confirm(`Remove attachment "${file.file_name}"?`)) return;
    try {
      await unlinkChangeOrderAttachment(file.id);
      setManagedAttachments((prev) => prev.filter((item) => item.id !== file.id));
      setUploadedFiles((prev) => prev.filter((item) => item.id !== file.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove attachment.');
    }
  }, []);

  const handleInvoiceUpload = useCallback(async (files: FileList) => {
    if (!co || !user || files.length === 0 || !invoiceFieldsAvailable) return;

    setInvoiceUploadLoading(true);
    setInvoiceUploadError('');

    try {
      const result = await uploadChangeOrderInvoiceFile({
        file: files[0],
        projectId: co.project_id,
        changeOrderId: co.id,
        userId: user.id,
      });

      if (result.error || !result.record) {
        throw new Error(result.error || 'Invoice upload failed');
      }

      setInvoiceFileId(result.record.id);
      setInvoiceFileName(result.record.file_name);
      setInvoiceFileUrl(result.record.file_url);
      setInvoiceFileStorageBucket(result.record.storage_bucket);
      setInvoiceFileStoragePath(result.record.storage_path);
    } catch (err) {
      setInvoiceUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setInvoiceUploadLoading(false);
    }
  }, [co, user, invoiceFieldsAvailable]);

  const handleOpenInvoiceFile = useCallback(async () => {
    if (!invoiceFileUrl && !invoiceFileStoragePath) return;
    const result = await openProjectFileInNewTab({
      id: invoiceFileId || undefined,
      file_url: invoiceFileUrl,
      storage_bucket: invoiceFileStorageBucket,
      storage_path: invoiceFileStoragePath,
      file_name: invoiceFileName,
    });
    if (!result.ok) {
      setInvoiceUploadError(result.error || 'Could not open invoice file. The file link may be missing or inaccessible.');
    }
  }, [invoiceFileUrl, invoiceFileStorageBucket, invoiceFileStoragePath, invoiceFileName, invoiceFileId]);

  async function handleVoidChangeOrder() {
    if (!co || !canVoid) return;
    const reason = voidReason.trim();
    if (!reason) { setVoidError('Cancellation reason is required.'); return; }
    setVoiding(true);
    setVoidError('');
    setError('');
    setSuccess('');
    const { error: rpcError } = await supabase.rpc('jphph_cancel_change_order', {
      p_change_order_id: co.id,
      p_reason: reason,
    });
    if (rpcError) { setVoidError(rpcError.message); setVoiding(false); return; }
    emitChangeOrdersSync(co.project_id);
    setSuccess('Change Order cancelled. Approval history has been retained.');
    setVoiding(false);
    onSaved();
    setTimeout(() => handleClose(), 800);
  }

  async function handleSave() {
    if (!co || !isValid) return;
    setSaving(true);
    setError('');
    setSuccess('');
    setCoNumberError('');

    // Validate duplicate CO number (exclude self)
    if (coNumber.trim() && coNumber.trim() !== (co.co_number || '')) {
      const isDuplicate = await checkDuplicateCoNumber(co.project_id, coNumber, co.id);
      if (isDuplicate) {
        setCoNumberError('This CO number already exists for this project.');
        setSaving(false);
        return;
      }
    }

    const invoicePayload = buildInvoicePayload(invoiceStatus, invoiceUrl, invoiceFileId, {
      invoice_status: co.invoice_status,
      invoiced_at: co.invoiced_at,
      paid_at: co.paid_at,
    });
    const invoiceChanged = hasInvoiceFieldChanges(
      { invoiceStatus, invoiceUrl, invoiceFileId },
      {
        invoice_status: co.invoice_status,
        invoice_url: co.invoice_url,
        invoice_file_id: co.invoice_file_id,
      }
    );

    if (invoiceChanged && !invoiceFieldsAvailable) {
      setError(CO_INVOICE_MIGRATION_HINT);
      setSaving(false);
      return;
    }

    const pricingResult = persistFormPricing(builderCost, clientPrice, pricingType, {
      clientRequired: requiresClientApproval,
    });
    if (pricingResult.error) {
      setClientPriceError(pricingResult.error);
      setError(pricingResult.error);
      setSaving(false);
      return;
    }
    setClientPriceError(null);
    setBuilderCostError(null);

    const commercial = {
      builder_cost: pricingResult.pricing.builder_cost,
      client_price: pricingResult.pricing.client_price,
      internal_note: internalNote.trim() || null,
    };

    // Approved / closed: only commercial fields. Status and approval history stay unchanged.
    const payload: Record<string, unknown> = locked
      ? buildLockedCommercialUpdate({
          ...commercial,
          invoice: invoiceFieldsAvailable ? invoicePayload : undefined,
        })
      : {
          title: title.trim(),
          description: description.trim() || null,
          client_description: clientDescription.trim() || null,
          reason: reason.trim() || null,
          current_finish_description: currentFinish.trim() || null,
          new_finish_description: newFinish.trim() || null,
          schedule_impact: scheduleImpact.trim() || null,
          ...commercial,
          status,
          requires_client_approval: requiresClientApproval,
          client_visible: clientVisible,
          co_number: coNumber.trim() || co.co_number,
          updated_at: new Date().toISOString(),
          ...(invoiceFieldsAvailable ? invoicePayload : {}),
        };

    let { error: updateErr } = await supabase
      .from('jphph_change_orders')
      .update(payload)
      .eq('id', co.id);

    if (updateErr && invoiceFieldsAvailable && invoiceChanged && isInvoiceColumnError(updateErr.message)) {
      setError(CO_INVOICE_MIGRATION_HINT);
      setSaving(false);
      return;
    }

    if (updateErr && isInvoiceColumnError(updateErr.message)) {
      const fallbackPayload = { ...payload };
      delete fallbackPayload.invoice_status;
      delete fallbackPayload.invoice_url;
      delete fallbackPayload.invoice_file_id;
      delete fallbackPayload.invoiced_at;
      delete fallbackPayload.paid_at;
      ({ error: updateErr } = await supabase
        .from('jphph_change_orders')
        .update(fallbackPayload)
        .eq('id', co.id));
      if (!updateErr && invoiceChanged) {
        setError(CO_INVOICE_MIGRATION_HINT);
        setSaving(false);
        return;
      }
    }

    if (updateErr) {
      setError(updateErr.message);
      setSaving(false);
      return;
    }

    // Link any newly uploaded files
    if (uploadedFiles.length > 0) {
      for (const f of uploadedFiles) {
        await supabase.from('jphph_project_files')
          .update({ change_order_id: co.id })
          .eq('id', f.id);
      }
    }

    setSuccess('Change Order updated successfully');
    setSaving(false);
    emitChangeOrdersSync(co.project_id);
    onSaved();
    // Keep modal open briefly so user sees success, then auto-close
    setTimeout(() => {
      handleClose();
    }, 800);
  }

  if (!show || !co) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end lg:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      <div className="relative bg-white w-full max-w-full lg:max-w-xl rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col" style={{ boxSizing: 'border-box' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[#1A1A1A] font-bold text-base">Edit Change Order</h3>
            {co.co_number && (
              <span className="text-xs font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-lg">{co.co_number}</span>
            )}
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          {/* Locked banner */}
          {locked && (
            <div className="bg-[#F9FAFB] rounded-xl p-3 border border-gray-100 flex items-start gap-2">
              <i className="ri-lock-line text-[#9CA3AF] text-sm flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="text-[#9CA3AF] text-xs font-semibold">Approved or closed change orders are partially locked.</p>
                <p className="text-[#9CA3AF] text-[11px]">Finish descriptions, status and approval history stay locked. Client Price / Credit, builder cost, internal notes and invoice fields remain editable.</p>
              </div>
            </div>
          )}

          {/* Pending approval warning */}
          {pendingApproval && (
            <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDBA74] flex items-start gap-2">
              <i className="ri-alarm-warning-line text-[#E8621A] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#92400E] text-xs">
                This Change Order is pending client approval. Editing it may require the client to review the updated details.
              </p>
            </div>
          )}

          {/* Validation errors */}
          {validationErrors.length > 0 && (
            <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA] space-y-1">
              <div className="flex items-center gap-1.5">
                <i className="ri-error-warning-line text-[#EF4444] text-sm"></i>
                <p className="text-[#EF4444] text-xs font-semibold">Please fix the following:</p>
              </div>
              <ul className="list-disc list-inside text-[#EF4444] text-[11px] space-y-0.5">
                {validationErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="bg-[#F0FDF4] rounded-xl p-3 border border-[#BBF7D0] flex items-center gap-2">
              <i className="ri-check-line text-[#059669] text-sm"></i>
              <p className="text-[#059669] text-xs font-semibold">{success}</p>
            </div>
          )}

          {/* Supabase error */}
          {error && (
            <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA] flex items-start gap-2">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#EF4444] text-xs">{error}</p>
            </div>
          )}

          {/* CO Number */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">
              CO Number <span className="text-[#EF4444]">*</span>
            </label>
            <input
              type="text"
              value={coNumber}
              onChange={(e) => {
                setCoNumberTouched(true);
                setCoNumber(e.target.value);
                setCoNumberError('');
                setError('');
              }}
              placeholder="e.g. CO-001"
              disabled={locked}
              className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
            />
            {coNumberError && (
              <p className="text-[#EF4444] text-[11px] mt-1">{coNumberError}</p>
            )}
            {coNumberTouched && !coNumberError && !locked && (
              <p className="text-[#9CA3AF] text-[11px] mt-1">
                <i className="ri-information-line mr-0.5"></i>
                Changing the CO number may affect client reference. Use carefully.
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">
              Title <span className="text-[#EF4444]">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setError(''); }}
              placeholder="Change order title"
              disabled={locked}
              className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Internal Description */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">
              Internal Description
              <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">(internal only)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => { setDescription(e.target.value); setError(''); }}
              placeholder="Internal description..."
              rows={2}
              disabled={locked}
              className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Client Description */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">
              Client Description
              <span className="ml-1 text-[10px] font-normal text-[#059669]">(client-visible)</span>
            </label>
            <textarea
              value={clientDescription}
              onChange={(e) => { setClientDescription(e.target.value); setError(''); }}
              placeholder="Description visible to the client..."
              rows={2}
              disabled={locked}
              className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
            />
            {locked && <p className="text-[10px] text-[#9CA3AF] mt-1">Locked — cannot edit client-facing description on approved/closed COs</p>}
          </div>

          {/* Reason for Change */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">
              Reason for Change <span className="text-[#EF4444]">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError(''); }}
              placeholder="Why is this change needed?"
              disabled={locked}
              className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Current / New Finish */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                Current Finish <span className="text-[#EF4444]">*</span>
              </label>
              <input
                type="text"
                value={currentFinish}
                onChange={(e) => { setCurrentFinish(e.target.value); setError(''); }}
                placeholder="What was originally planned"
                disabled={locked}
                className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
              />
            </div>
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                New Finish <span className="text-[#EF4444]">*</span>
              </label>
              <input
                type="text"
                value={newFinish}
                onChange={(e) => { setNewFinish(e.target.value); setError(''); }}
                placeholder="What is being changed to"
                disabled={locked}
                className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          {/* Schedule Impact */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">
              Schedule Impact
              <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">(client-visible)</span>
            </label>
            <input
              type="text"
              value={scheduleImpact}
              onChange={(e) => { setScheduleImpact(e.target.value); setError(''); }}
              placeholder="e.g. +3 days or No impact"
              disabled={locked}
              className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
            />
            {locked && <p className="text-[10px] text-[#9CA3AF] mt-1">Locked — cannot edit schedule impact on approved/closed COs</p>}
          </div>

          {/* Pricing — Client Price always editable for management */}
          <ChangeOrderPricingFields
            idPrefix="edit-co"
            pricingType={pricingType}
            onPricingTypeChange={(type) => {
              setPricingType(type);
              setError('');
              setClientPriceError(null);
              setBuilderCostError(null);
            }}
            pricingMode={pricingMode}
            onPricingModeChange={(mode) => {
              setPricingMode(mode);
              setError('');
              setClientPriceError(null);
            }}
            builderCost={builderCost}
            onBuilderCostChange={(value) => {
              setBuilderCost(value);
              setError('');
              setBuilderCostError(null);
            }}
            clientPrice={clientPrice}
            onClientPriceChange={(value) => {
              setClientPrice(value);
              setError('');
              setClientPriceError(null);
            }}
            showLockedHint={locked}
            clientPriceError={clientPriceError}
            builderCostError={builderCostError}
          />

          {/* Internal Note */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">
              Internal Note
              <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">(internal only)</span>
            </label>
            <textarea
              value={internalNote}
              onChange={(e) => { setInternalNote(e.target.value); setError(''); }}
              placeholder="Any internal notes..."
              rows={2}
              className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none focus:outline-none focus:border-[#E8621A]"
            />
          </div>

          {/* Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value); setError(''); }}
                disabled={locked}
                className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] cursor-pointer ${locked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
              >
                {Object.entries(EDITABLE_STATUSES_LABELS).map(([val, lbl]) => (
                  <option key={val} value={val}>{lbl}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2 justify-center">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_requires_client_approval"
                  checked={requiresClientApproval}
                  onChange={(e) => setRequiresClientApproval(e.target.checked)}
                  disabled={locked}
                  className="w-4 h-4 accent-[#E8621A] cursor-pointer"
                />
                <label htmlFor="edit_requires_client_approval" className="text-xs text-[#374151] cursor-pointer">
                  Requires Client Approval
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_client_visible"
                  checked={clientVisible}
                  onChange={(e) => setClientVisible(e.target.checked)}
                  disabled={locked}
                  className="w-4 h-4 accent-[#E8621A] cursor-pointer"
                />
                <label htmlFor="edit_client_visible" className="text-xs text-[#374151] cursor-pointer">
                  Client Visible
                </label>
              </div>
            </div>
          </div>

          {canVoid && (
            <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-4 space-y-3">
              <div className="flex items-start gap-2">
                <i className="ri-forbid-2-line text-[#DC2626] mt-0.5"></i>
                <div>
                  <p className="text-sm font-bold text-[#991B1B]">Cancel / Void approved Change Order</p>
                  <p className="text-[11px] text-[#B91C1C] mt-0.5">The CO and approval history are retained. Cancelled COs are removed from active financial totals.</p>
                </div>
              </div>
              <textarea value={voidReason} onChange={(e) => { setVoidReason(e.target.value); setVoidError(''); }} placeholder="Cancellation reason (required)" rows={2} maxLength={500} className="w-full rounded-xl border border-[#FCA5A5] bg-white px-3 py-2.5 text-sm text-[#1A1A1A] resize-none focus:outline-none focus:border-[#DC2626]" />
              {voidError && <p className="text-[11px] text-[#DC2626]">{voidError}</p>}
              <button type="button" onClick={handleVoidChangeOrder} disabled={voiding || !voidReason.trim()} className="w-full py-2.5 rounded-xl bg-[#DC2626] text-white text-xs font-bold disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2">
                {voiding ? <><i className="ri-loader-4-line animate-spin"></i>Cancelling...</> : <><i className="ri-forbid-2-line"></i>Cancel / Void Change Order</>}
              </button>
            </div>
          )}

          {/* Invoice / Billing — internal only */}
          <div className="rounded-xl border border-[#E0DDD8] p-4 space-y-3 bg-[#FAFAFA]">
            <div>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Client Invoice</p>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">Internal billing link — not shown to client unless explicitly shared elsewhere.</p>
            </div>

            {!invoiceFieldsAvailable && (
              <div className="bg-[#FFFBEB] rounded-xl px-3 py-2.5 border border-[#FDE68A] text-[#92400E] text-xs">
                {CO_INVOICE_MIGRATION_HINT}
              </div>
            )}

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Invoice Status</label>
              <select
                value={invoiceStatus}
                onChange={(e) => setInvoiceStatus(e.target.value as ChangeOrderInvoiceStatus)}
                disabled={!invoiceFieldsAvailable}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
              >
                {CO_INVOICE_STATUSES.map((status) => (
                  <option key={status} value={status}>{CO_INVOICE_STATUS_LABELS[status]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                Invoice URL
                <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">(e.g. Google Drive)</span>
              </label>
              <input
                type="url"
                value={invoiceUrl}
                onChange={(e) => setInvoiceUrl(e.target.value)}
                placeholder="https://drive.google.com/..."
                disabled={!invoiceFieldsAvailable}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] disabled:bg-gray-50 disabled:text-gray-400"
              />
              {invoiceUrl.trim() && (
                <a
                  href={invoiceUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#E8621A] text-xs font-semibold mt-1.5 hover:underline"
                >
                  <i className="ri-external-link-line" />
                  Open invoice link
                </a>
              )}
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Link Project File</label>
              <select
                value={invoiceFileId || ''}
                onChange={(e) => {
                  const nextId = e.target.value || null;
                  setInvoiceFileId(nextId);
                  const selected = projectFileOptions.find((f) => f.id === nextId);
                  setInvoiceFileName(selected?.file_name || '');
                  setInvoiceFileUrl(selected?.file_url || '');
                  setInvoiceFileStorageBucket(selected?.storage_bucket ?? null);
                  setInvoiceFileStoragePath(selected?.storage_path ?? null);
                }}
                disabled={!invoiceFieldsAvailable}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">No linked file</option>
                {projectFileOptions.map((file) => (
                  <option key={file.id} value={file.id}>{file.file_name}</option>
                ))}
              </select>
            </div>

            {(invoiceFileName || invoiceFileUrl) && (
              <div className="flex items-center justify-between gap-2 bg-white rounded-xl border border-gray-100 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#374151] truncate">{invoiceFileName || 'Linked invoice file'}</p>
                  <button
                    type="button"
                    onClick={() => void handleOpenInvoiceFile()}
                    className="text-[10px] text-[#E8621A] font-semibold hover:underline inline-flex items-center gap-1 mt-0.5 cursor-pointer"
                  >
                    <i className="ri-external-link-line" />
                    View invoice
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setInvoiceFileId(null);
                    setInvoiceFileName('');
                    setInvoiceFileUrl('');
                    setInvoiceFileStorageBucket(null);
                    setInvoiceFileStoragePath(null);
                  }}
                  className="text-[10px] text-[#9CA3AF] hover:text-[#EF4444] cursor-pointer whitespace-nowrap"
                >
                  Clear
                </button>
              </div>
            )}

            <InlineUploadDropzone
              accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
              multiple={false}
              allowCamera={false}
              disabled={!invoiceFieldsAvailable}
              loading={invoiceUploadLoading}
              error={invoiceUploadError}
              title="Drop invoice file here or click to choose"
              hint="PDF or image · replaces linked invoice only after upload succeeds"
              onFilesSelected={handleInvoiceUpload}
            />
          </div>

          {/* Existing Attachments */}
          <div>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">Existing Attachments</p>
            <ChangeOrderAttachmentList
              files={managedAttachments}
              onDelete={handleDeleteManagedAttachment}
              onVisibilityUpdated={handleManagedVisibilityUpdated}
              emptyText="No attachments yet."
            />
          </div>

          {/* Add attachments button */}
          <button
            onClick={() => setShowFileUploader(true)}
            className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-[#9CA3AF] text-sm font-medium hover:border-[#E8621A] hover:text-[#E8621A] cursor-pointer transition-colors flex items-center justify-center gap-2"
          >
            <i className="ri-attachment-line"></i>
            {uploadedFiles.length > 0 ? `${uploadedFiles.length} new file${uploadedFiles.length !== 1 ? 's' : ''} — Add more` : 'Attach Files (Images or PDF)'}
          </button>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          <button
            onClick={handleSave}
            disabled={saving || !isValid}
            className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
            ) : (
              <><i className="ri-save-line"></i>Save Changes</>
            )}
          </button>
          {!isValid && validationErrors.length > 0 && (
            <p className="text-center text-[10px] text-[#EF4444]">{validationErrors[0]}</p>
          )}
          <button
            onClick={handleClose}
            className="w-full py-2.5 rounded-xl border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* File Uploader */}
      {showFileUploader && co && (
        <FileUploader
          projectId={co.project_id}
          currentUserId={user?.id || ''}
          open={showFileUploader}
          onClose={() => setShowFileUploader(false)}
          onUploadComplete={handleFileUploadComplete}
          accountType="management"
          category="change_order"
          allowPdf={true}
          allowMultiple={true}
          allowCamera={true}
          enablePerFileCaption={true}
          showVisibilitySelector={true}
          visibilityScope="management"
          bucket="change-order-photos"
          relatedType="change_order"
          relatedId={co.id}
          title="Attach Change Order Files"
        />
      )}

    </div>
  );
}